<template>
  <div
    class="detail-slider relative bg-scheme-main"
    :class="{
      'has-lightbox-open': isOpen,
    }"
  >
    <div
      class="pointer-events-none absolute top-3 right-3 z-30 rounded bg-black/40 p-1"
    >
      <SpriteSymbol name="expand" class="size-6 text-white" />
    </div>

    <div ref="flickingEl" class="flicking-viewport">
      <div class="flicking-camera">
        <a
          v-for="(image, index) in galleryImages"
          :key="index"
          class="mr-3 w-full cursor-zoom-in"
          :href="image.url"
          :data-pswp-width="image.width"
          :data-pswp-height="image.height"
          :data-title="image.title"
          draggable="false"
          tabindex="-1"
        >
          <MediaImage
            v-bind="image"
            image-class="aspect-video object-cover"
            responsive-style="hero_slider"
            no-draggable
          />
          <span class="sr-only">{{ image.title }}</span>
        </a>
      </div>
      <div v-if="isEnabled" class="absolute bottom-0 left-0 z-30 w-full p-3">
        <div class="flicking-pagination flex gap-2" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  MediaImageFragment,
  MediaCantoFragment,
} from '#graphql-operations'
import { getCantoImageStyle } from '~/helpers/canto'
import { responsiveStylesMap } from '~/helpers/responsiveStyles'

const props = defineProps<{
  nid?: string
  images: MediaImageFragment[] | MediaCantoFragment[]
}>()

const flickingEl = ref<HTMLDivElement>()

const persistKey = computed(() =>
  props.nid ? 'detail-slider-' + props.nid : null,
)

const isEnabled = computed(() => props.images.length > 1)

const { moveTo } = await useFlicking(flickingEl, {
  flickingOptions: {
    align: 'prev',
    circular: true,
  },
  persistKey,
  isEnabled,
  useAutoplayPagination: true,
})

const { isOpen } = await useLightbox(flickingEl, {
  onSlideChange(index) {
    moveTo(index, 0)
  },
})

const galleryImages = computed(() => {
  return props.images.map((image) => {
    if (image.__typename === 'MediaImage') {
      return {
        ...image,
        url: image.fileUrl?.path,
        width: image.image?.width,
        height: image.image?.height,
        title: image.image?.title || image.image?.alt,
      }
    } else if (image.__typename === 'MediaCanto') {
      const responsiveStyles = responsiveStylesMap.get('hero_slider')
      const imageStyle = responsiveStyles?.styles?.[0]?.id || '-S300x225'
      return {
        ...image,
        title: image.titleCanto,
        ...getCantoImageStyle(
          image.assetId!,
          { imageStyle },
          image.renderUrl ?? '',
        ),
      }
    }
    return image
  })
})
</script>
